import type {LDFlagValue} from 'launchdarkly-js-client-sdk';
import {useFlags} from 'launchdarkly-react-client-sdk';
import type {LDFlagSet} from 'launchdarkly-react-client-sdk';

import BenchlingConfig from 'BenchlingConfig';
import {isNodeTesting} from 'lib/Browser';
import {useAppSelector} from 'redux-stores/reduceApp';
import * as logging from 'util/Logging';

/**
 * Converts SHOUTY_SNAKE_CASE to camelCase. E.g. MY_FEATURE_FLAG -> myFeatureFlag.
 *
 * Assumes that the input is in SHOUTY_SNAKE_CASE format.
 */
function shoutySnakeToCamelCase(input: string): string {
  // Shouty snake case regex
  if (!/^[A-Z][A-Z0-9]*(_[A-Z0-9]+)*$/.test(input)) {
    throw new Error(`Input is not SHOUTY_SNAKE_CASE: "${input}"`);
  }
  return input.toLowerCase().replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
}

export interface LaunchDarklyContext {
  kind: 'user';
  key: string;
  name: string;
  subdomain: string;
  stack: string;
  deploy: string;
  environment: string;
}

const BROWSER_TESTING_PREFIX = 'browsertesting';

/**
 * Gets the User object for LaunchDarkly. This object represents a tenant, and contains several infra-related
 * fields relevant to a tenant: friendly name, subdomain, stack name, deploy name, and deploy environment.
 * It also contains the tenant's API ID.
 *
 * Infra attributes included in the context:
 * - `subdomain`: the tenant's subdomain (e.g. "revmed.benchling.com")
 * - `stack`: the stack name (e.g. "main")
 * - `deploy`: the deploy name sourced from DEPLOY_NAME (e.g. "main")
 * - `environment`: the deploy environment sourced from DEPLOY_ENVIRONMENT (e.g. "latest", "stable")
 *
 * Both `deploy` and `environment` are included to match the backend LaunchDarkly context. `deploy` identifies
 * which named deploy the tenant is on, while `environment` identifies the release stage of that deploy.
 *
 * If called in a browser testing context, most of the fields will be clobbered with `browsertesting` in order
 * to avoid running up the LD bill, which is calculated per-unique-user.
 */
export function getLaunchDarklyContext(): LaunchDarklyContext {
  const isBrowserTesting = BenchlingConfig.TENANT_SUBDOMAIN.startsWith(BROWSER_TESTING_PREFIX);

  return {
    kind: 'user',
    key: isBrowserTesting ? BROWSER_TESTING_PREFIX : BenchlingConfig.TENANT_ID,
    name: isBrowserTesting ? BROWSER_TESTING_PREFIX : BenchlingConfig.TENANT_NAME,
    subdomain: isBrowserTesting ? BROWSER_TESTING_PREFIX : BenchlingConfig.TENANT_SUBDOMAIN,
    stack: BenchlingConfig.STACK_NAME,
    deploy: BenchlingConfig.DEPLOY_NAME,
    environment: BenchlingConfig.DEPLOY_ENVIRONMENT,
  };
}

const useMockLDFlags = (): LDFlagSet => useAppSelector((state) => state.ui.mockLaunchDarkly.flags);

/**
 * `useRolloutFlags` is a custom wrapper around LaunchDarkly's `useFlags` which returns all feature flags provided
 * by `LaunchDarklyProvider`. During a unit test (when `isNodeTesting` is true), it will instead read from the Redux store.
 *
 * This is necessary because we don't want to access the live LD service while unit testing and we can't mock
 * `useFlags` directly because the canonical way to mock LD relies on Jest's ability to globally mock modules,
 * which Sinon / proxyquire cannot do.
 *
 * @return A set of feature flag values
 */
export const useRolloutFlags = isNodeTesting ? useMockLDFlags : useFlags;

/**
 * Evaluate an LD flag outside React and Backbone. Waits for the client to initialize
 * if needed (up to 5s, matching LaunchDarklyFlagsCollection), then reads the flag value.
 * Returns the defaultValue on timeout or error.
 */
export async function evaluateLDFlag(
  flagName: string,
  defaultValue: LDFlagValue = false
): Promise<LDFlagValue> {
  if (!window.ldClient!.ready) {
    await window.ldClient!.waitForInitialization(5).catch((err) => {
      logging.warn(
        `LDClient initialization failed or timed out; falling back to default for ${flagName}. Original error: ${err}`
      );
    });
  }
  return window.ldClient!.variation(flagName, defaultValue);
}

/**
 * Converts a set of LaunchDarkly flags with SHOUTY_SNAKE_CASE keys to camelCase keys.
 */
export function camelCaseFlagSet(shoutyFlags: LDFlagSet): LDFlagSet {
  const flags: LDFlagSet = {};

  Object.keys(shoutyFlags).forEach((k) => {
    const camel = shoutySnakeToCamelCase(k);
    flags[camel] = shoutyFlags[k];
  });

  return flags;
}
